<template>
  <section class="authentication">
    <div class="authentication__background" />
    <div class="reset-password">
      <h2 class="reset-password__title">{{ oobCode ? 'reset password' : 'forgot password' }}</h2>
      <!-- Set-new-password form: shown when arriving from the email link (oobCode present) -->
      <v-form
        v-if="oobCode"
        v-model="valid"
        class="wrap"
        @submit.prevent="submitNewPassword"
      >
        <v-text-field
          v-model="newPassword"
          :rules="[rules.required]"
          autocomplete="off"
          background-color="#fff"
          class="reset-password__field"
          dense
          label="New password*"
          outlined
          type="password"
        />
        <v-text-field
          v-model="confirmNewPassword"
          :rules="[rules.required]"
          autocomplete="off"
          background-color="#fff"
          class="reset-password__field"
          dense
          label="Confirm new password*"
          outlined
          type="password"
        />
        <span
          v-show="error"
          class="reset-password__text-error"
          >{{ error }}</span
        >
        <span class="reset-password__text-separate">Enter your new password</span>
        <v-btn
          :disabled="btnDisabled"
          :loading="btnLoading"
          block
          class="reset-password__btn"
          color="var(--color-blue-800)"
          height="48"
          outlined
          tile
          @click="submitNewPassword"
        >
          Submit
        </v-btn>
      </v-form>
      <v-form
        v-else-if="!isShowFinishMessage"
        v-model="valid"
        class="wrap"
        @submit.prevent="changePassword"
      >
        <v-text-field
          v-model="email"
          :rules="[rules.required, rules.email]"
          autocomplete="off"
          background-color="#fff"
          class="reset-password__field"
          dense
          label="Email*"
          outlined
          @input="$nextTick(() => (email = email.trim()))"
        />
        <span
          v-show="error"
          class="reset-password__text-error"
          >{{ error }}</span
        >
        <span class="reset-password__text-separate">Enter email address on the account</span>
        <v-btn
          :disabled="btnDisabled"
          :loading="btnLoading"
          block
          class="reset-password__btn"
          color="var(--color-blue-800)"
          height="48"
          outlined
          tile
          @click="changePassword"
        >
          Submit
        </v-btn>
      </v-form>
      <div
        v-else
        class="wrap"
      >
        <span class="reset-password__text-separate">Check your inbox for a password reset email</span>
        <br />
        <router-link
          class="reset-password__link"
          to="/login"
        >
          login page
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
import { getAuth, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth'
import { mapActions } from 'vuex'
import validationRules from '@/mixins/validation'
import { userApi } from '@/enitites/user'

export default {
  mixins: [validationRules],
  data() {
    return {
      valid: true,
      isShowFinishMessage: false,
      email: null,
      error: null,
      btnDisabled: true,
      btnLoading: false,
      // Set-new-password flow (arriving from the reset email link)
      oobCode: null,
      newPassword: '',
      confirmNewPassword: '',
    }
  },
  watch: {
    valid(value) {
      this.btnDisabled = !value
    },
  },
  created() {
    // When the user clicks the reset email link they land here with ?oobCode=...
    // Its presence switches the component from the "enter email" form to the
    // "set new password" form.
    this.oobCode = this.$route.query.oobCode || null
    if (this.oobCode) {
      // A valid code should not be disabled by the email-form's watcher default.
      this.btnDisabled = false
    }

    if (this.$store.state.browserHistoryState) {
      // eslint-disable-next-line no-restricted-globals
      history.replaceState(this.$store.state.browserHistoryState, 'Prev page')
    }
  },
  methods: {
    ...mapActions(['showAlertToast']),
    async changePassword() {
      if (!this.email) {
        this.error = 'Field is required'
        return
      }
      this.btnLoading = true

      const { data: isEmailExist } = await userApi.resetPassword(this.email)
      if (!isEmailExist) {
        this.btnLoading = false
        this.email = null
        return
      }
      this.showAlertToast('emailReset')
      // eslint-disable-next-line no-restricted-globals
      history.back()
    },
    async submitNewPassword() {
      this.error = null

      if (!this.newPassword || !this.confirmNewPassword) {
        this.error = 'All fields are required'
        return
      }
      if (this.newPassword !== this.confirmNewPassword) {
        this.error = 'Passwords must be equal'
        return
      }

      this.btnLoading = true
      try {
        const auth = getAuth()
        // Validates that the oobCode is genuine and unexpired before we consume it.
        await verifyPasswordResetCode(auth, this.oobCode)
        await confirmPasswordReset(auth, this.oobCode, this.newPassword)
        this.showAlertToast('changesSaved')
        this.$router.push('/login')
      } catch (err) {
        this.error = err.message
      } finally {
        this.btnLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.authentication {
  height: 100%;
  background-color: rgb(255 255 255);

  &__background {
    height: 488px;
    background: #fff url('/img/HeroBG.jpg') 50% 95% / cover no-repeat;
  }
}

.reset-password {
  position: relative;
  width: 100%;
  max-width: 410px;
  min-height: 500px;
  margin: -300px auto 0;
  padding: 0 20px;
  text-align: center;

  &__title {
    @include font-h2;

    margin-bottom: 80px;
    text-align: center;
    text-transform: uppercase;
  }

  &__field.v-text-field {
    margin-bottom: -20px;
  }

  &__btn {
    margin-bottom: 12px;
    font-weight: bold;
    border: 2px solid;
  }

  &__text-separate {
    display: inline-block;
    margin-top: 12px;
    margin-bottom: 20px;
  }

  &__text-error {
    display: block;
    margin-bottom: 15px;
    color: red;
  }

  &__link {
    color: #38a1f3;
    text-decoration: none;
  }
}
</style>
